import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useForgotPassword } from '@/containers/Login/hooks/useAuth';
import { ROUTES } from '@/routes/config';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const forgotPassword = useForgotPassword();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await forgotPassword
      .mutateAsync(email)
      .then(() => setSubmitted(true))
      .catch(() => {});
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography component="h1" variant="h5" sx={{ mb: 1 }}>
            Forgot Password
          </Typography>

          {submitted ? (
            <>
              <Alert severity="success" sx={{ width: '100%', mb: 2, mt: 2 }}>
                If an account with that email exists, a password reset link has been sent. Please check your inbox.
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                The link will expire in 15 minutes. If you don't see the email, check your spam folder.
              </Typography>
              <Link component={RouterLink} to={ROUTES.LOGIN} variant="body2">
                Back to Sign In
              </Link>
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                Enter your email address and we'll send you a link to reset your password.
              </Typography>

              {forgotPassword.error && (
                <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                  {(forgotPassword.error as Error).message || 'Something went wrong. Please try again.'}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="Email Address"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={forgotPassword.isPending}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 2, mb: 2 }}
                  disabled={!email.trim() || forgotPassword.isPending}
                  startIcon={forgotPassword.isPending ? <CircularProgress size={20} /> : null}
                >
                  {forgotPassword.isPending ? 'Sending...' : 'Send Reset Link'}
                </Button>
                <Box sx={{ textAlign: 'center' }}>
                  <Link component={RouterLink} to={ROUTES.LOGIN} variant="body2">
                    Back to Sign In
                  </Link>
                </Box>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ForgotPassword;
